import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import RefLink from './RefLink';
import { FormModal, btnGhost } from './FormKit';
import WorkflowStatusBadge from '../workflow/WorkflowStatusBadge';
import { ACTION_LABEL } from '../workflow/constants';
import { DetailGrid, DetailItem } from '../workflow/SectionCard';
import { formatDateTime } from '../../utils/dateFormat';
import { actorLabel } from '../workflow/eventActor';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// docs/conventions.md §2「檢視與審核資料的呈現原則」— the source application of
// a formal record (engagement / client entity / QC record) is a BUTTON that
// expands the application summary in place: requester, timing, key fields and
// the request timeline. A link to the full request page is supplementary, never
// the only way in. Email events are business-noise and are filtered out.
//
// Props:
//   title     — modal heading (e.g. 'Source application · ENG-3')
//   fetchPath — API path returning { request, events } (e.g.
//               /api/engagements/5/source-request)
//   fields    — (request) => [[label, value]] rows for the summary grid
//   openHref  — optional route to the full request page ("Open full request")
//   onClose
// Status/label vocabulary comes from the unified workflow kit — the chip now
// shows the ratified wording (e.g. "Pending review") instead of the raw value.
export const StatusChip = ({ value }) => <WorkflowStatusBadge status={value} size="chip" />;

const SourceRequestModal = ({ title, fetchPath, fields, openHref, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}${fetchPath}`, { credentials: 'include' });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `Request failed (${res.status})`);
        if (!cancelled) setData(body);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [fetchPath]);

  const request = data?.request || null;
  // House rule: history never shows email events.
  const events = (data?.events || []).filter((e) => e.action !== 'email_sent');

  return (
    <FormModal
      title={title}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          {openHref && request && (
            <RefLink to={openHref} className={`${btnGhost} inline-flex items-center gap-1.5`}>
              <ExternalLink className="w-4 h-4" /> Open Full Request
            </RefLink>
          )}
          <button type="button" onClick={onClose} className={btnGhost}>Close</button>
        </>
      )}
    >
      {error ? (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
          {error}
        </div>
      ) : !request ? (
        <p className="text-sm text-slate-500 py-2">Loading…</p>
      ) : (
        <>
          <DetailGrid>
            {fields(request).map(([label, value]) => (
              <DetailItem key={label} label={label}>{value}</DetailItem>
            ))}
          </DetailGrid>
          <div className="mt-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-2">Request History</h4>
            {events.length === 0 ? (
              <p className="text-sm text-slate-500">No history.</p>
            ) : (
              <ol className="relative border-l border-slate-200 ml-1.5 space-y-3">
                {events.map((e) => (
                  <li key={e.id} className="ml-4">
                    <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-slate-300" />
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-slate-800">{(ACTION_LABEL[e.action] || [e.action])[0]}</span>
                      {e.actorEmail && <span className="text-xs text-slate-500" title={e.actorName ? e.actorEmail : undefined}>{actorLabel(e)}</span>}
                      <span className="text-xs text-slate-500">{formatDateTime(e.createdAt)}</span>
                    </div>
                    {e.reason && <p className="text-xs text-slate-500 mt-0.5 break-words">{e.reason}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </>
      )}
    </FormModal>
  );
};

export default SourceRequestModal;
